import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { ArrowLeft, ExternalLink, Copy, Info, FileText, KeyRound, Eye, Globe, Sparkles } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { organizationService, demoDataService } from '../api';
import { Button, Input, ThemedText } from '../components';
import { FormGroup, FormLabel as Label } from '../components/FormStyles';
import { ToggleSwitch, ToggleInput, ToggleSlider } from '../components/ToggleStyles';
import DemoCredentialsPanel from '../components/DemoCredentialsPanel';
import DemoDomainPanel from '../components/DemoDomainPanel';
import { parseDemoLanding, demoPublicUrl, demoLaunchUrl, demoBuilderEmail, demoViewerEmailDefault, DEMO_VIEWER_DEFAULT_PASSWORD } from '../utils/demoStudio';

const PageWrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background: none;
  border: none;
  color: ${props => props.theme.colors.tint};
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.default};
  padding: 0;
  margin-bottom: ${props => props.theme.spacing.md};
  &:hover { text-decoration: underline; }
`;

const FormCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
`;

const SubTabBar = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  width: fit-content;
  flex-wrap: wrap;
`;

const SubTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: ${({ $active, theme }) => ($active ? theme.colors.background : 'transparent')};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ $active, theme }) => ($active ? theme.colors.text : theme.colors.muted)};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const SectionTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-top: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  &:first-of-type { margin-top: 0; }
`;

const Hint = styled(ThemedText).attrs({ type: 'small' })`
  color: ${({ theme }) => theme.colors.muted};
  display: block;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.sizes.small};
  flex-wrap: wrap;
`;

const InlineLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.tint};
  word-break: break-all;
  &:hover { text-decoration: underline; }
`;

const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.tint};
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  &:hover { text-decoration: underline; }
`;

const BuilderInfo = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.sizes.small};
  color: ${({ theme }) => theme.colors.text};
  code {
    font-family: monospace;
    color: ${({ theme }) => theme.colors.tint};
    word-break: break-all;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
`;

const AiPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
`;

const AiLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.typography.sizes.small};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.tint};
`;

const AiBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
`;

const AiInputWrap = styled.div`
  flex: 1;
  min-width: 240px;
  /* Input wraps its field in a div with a bottom margin — drop it so the field lines up with the button. */
  > div { margin-bottom: 0; }
`;

const emptyForm = {
  name: '',
  demoSlug: '',
  // Viewer (public, read-only) credentials. The password is prefilled with the shared standard so
  // every demo's viewer login is the same (still editable for the rare custom demo).
  demoLoginEmail: '',
  demoLoginPassword: DEMO_VIEWER_DEFAULT_PASSWORD,
  headline: '',
  subheadline: '',
  logo: '',
  featuredTokens: '',
  published: false,
};

const DemoEditorPage = () => {
  const theme = useTheme();
  const { isSuperAdmin } = useRole();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const isEdit = !!orgId;

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState('details');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const org = await organizationService.getOrganizationById(orgId);
        const demo = parseDemoLanding(org.demoLandingJson);
        if (cancelled) return;
        setForm({
          name: org.name || '',
          demoSlug: org.demoSlug || '',
          demoLoginEmail: demo.demoLoginEmail || '',
          demoLoginPassword: demo.demoLoginPassword || '',
          headline: demo.headline || '',
          subheadline: demo.subheadline || '',
          logo: org.logo || '',
          featuredTokens: (demo.featuredShareTokens || []).join('\n'),
          published: !!demo.published,
        });
      } catch (error) {
        console.error('Failed to load demo:', error);
        toast.error(error?.message || 'Failed to load demo');
        navigate('/demo-studio');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, isEdit, navigate]);

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  // Slug drives the derived builder email and the default viewer email. Keep the
  // viewer email in sync with the slug default until the user edits it (create only).
  const handleSlugChange = (e) => {
    const newSlug = e.target.value;
    setForm(prev => {
      if (isEdit) return { ...prev, demoSlug: newSlug };
      const emailUntouched = !prev.demoLoginEmail || prev.demoLoginEmail === demoViewerEmailDefault(prev.demoSlug);
      return {
        ...prev,
        demoSlug: newSlug,
        demoLoginEmail: emailUntouched ? demoViewerEmailDefault(newSlug) : prev.demoLoginEmail,
      };
    });
  };

  const copy = (text, label = 'Copied!') => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  // One AI call fills the demo's own config text (name, slug, landing headline/subheadline) from a short
  // prompt or, if that's blank, the name already typed. Nothing saves until the user reviews and clicks Save.
  const handleAiFill = async () => {
    const desc = aiPrompt.trim() || form.name.trim();
    if (!desc) { toast.error('Enter a short description (or a name) for the AI to work from'); return; }
    setAiLoading(true);
    try {
      const s = await demoDataService.suggestDemoConfig(desc);
      setForm(prev => {
        const nextSlug = s.slug || prev.demoSlug;
        const emailUntouched = !isEdit && (!prev.demoLoginEmail || prev.demoLoginEmail === demoViewerEmailDefault(prev.demoSlug));
        return {
          ...prev,
          name: s.name || prev.name,
          demoSlug: nextSlug,
          headline: s.headline || prev.headline,
          subheadline: s.subheadline || prev.subheadline,
          demoLoginEmail: emailUntouched ? demoViewerEmailDefault(nextSlug) : prev.demoLoginEmail,
        };
      });
      toast.success('Demo details filled — review and adjust before saving');
    } catch (error) {
      console.error('AI demo fill failed:', error);
      toast.error(error?.response?.data?.message || error?.message || 'AI fill failed');
    } finally {
      setAiLoading(false);
    }
  };

  const buildLandingJson = () => JSON.stringify({
    headline: form.headline.trim(),
    subheadline: form.subheadline.trim(),
    featuredShareTokens: form.featuredTokens.split('\n').map(t => t.trim()).filter(Boolean),
    demoLoginEmail: form.demoLoginEmail.trim(),
    demoLoginPassword: form.demoLoginPassword,
    published: !!form.published,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const slug = form.demoSlug.trim();
    if (!slug) { toast.error('Demo slug is required'); return; }
    if (!form.name.trim()) { toast.error('Name is required'); return; }

    if (!isEdit) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.demoLoginEmail.trim())) { toast.error('A valid viewer login email is required'); return; }
      if (form.demoLoginPassword.length < 6) { toast.error('Viewer login password must be at least 6 characters'); return; }
    }

    setSaving(true);
    try {
      if (isEdit) {
        await organizationService.updateOrganization(orgId, {
          name: form.name.trim(),
          logo: form.logo.trim() || undefined,
          isDemo: true,
          demoSlug: slug,
          demoLandingJson: buildLandingJson(),
        });
        toast.success('Demo updated successfully');
      } else {
        await organizationService.createOrganization({
          name: form.name.trim(),
          logo: form.logo.trim() || undefined,
          // Builder (internal, read-write) account — email derived from slug.
          adminEmail: demoBuilderEmail(slug),
          adminFirstName: 'Demo',
          adminLastName: 'Admin',
          // IGNORED server-side for demos (the builder uses the shared config password);
          // sent only as a valid >=6 placeholder to satisfy create validation.
          adminPassword: form.demoLoginPassword,
          isDemo: true,
          demoSlug: slug,
          // Viewer (public, read-only) account — the credentials shown on the landing page.
          demoViewerEmail: form.demoLoginEmail.trim(),
          demoViewerPassword: form.demoLoginPassword,
          demoLandingJson: buildLandingJson(),
        });
        toast.success('Demo created successfully');
      }
      navigate('/demo-studio');
    } catch (error) {
      console.error('Failed to save demo:', error);
      toast.error(error?.message || 'Failed to save demo');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin()) {
    return (
      <Layout currentPath="/demo-studio">
        <PageWrap>
          <ThemedText type="title">Access Denied</ThemedText>
          <ThemedText>You do not have permission to access this page.</ThemedText>
        </PageWrap>
      </Layout>
    );
  }

  const slug = form.demoSlug.trim();

  return (
    <Layout currentPath={isEdit ? `/demo-studio/${orgId}` : '/demo-studio/new'}>
      <PageWrap>
        <BackButton theme={theme} onClick={() => navigate('/demo-studio')}>
          <ArrowLeft size={16} /> Back to Demo Studio
        </BackButton>

        <ThemedText type="title">{isEdit ? 'Edit Demo' : 'New Demo'}</ThemedText>
        <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.lg, display: 'block' }}>
          {isEdit
            ? 'Update this demo organization’s public landing page.'
            : 'Stand up a demo organization with a shareable login — no welcome or verification emails are sent.'}
        </ThemedText>

        {loading ? (
          <LoadingState theme={theme}><ThemedText>Loading demo...</ThemedText></LoadingState>
        ) : (
          <form onSubmit={handleSubmit}>
            <AiPanel theme={theme}>
              <AiLabel theme={theme}>
                <Sparkles size={14} /> Describe the business — AI fills the name, slug and landing copy below
              </AiLabel>
              <AiBar theme={theme}>
                <AiInputWrap>
                  <Input
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder={form.name
                      ? `Fill from “${form.name}”, or describe the business…`
                      : 'e.g. “vehicle inspection company in Vancouver”'}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAiFill(); } }}
                  />
                </AiInputWrap>
                <Button type="button" variant="secondary" onClick={handleAiFill} disabled={aiLoading}>
                  <Sparkles size={14} /> {aiLoading ? 'Filling…' : 'Fill with AI'}
                </Button>
              </AiBar>
            </AiPanel>

            <SubTabBar theme={theme}>
              <SubTab theme={theme} type="button" $active={section === 'details'} onClick={() => setSection('details')}>
                <Info size={14} /> Details
              </SubTab>
              <SubTab theme={theme} type="button" $active={section === 'landing'} onClick={() => setSection('landing')}>
                <FileText size={14} /> Landing
              </SubTab>
              <SubTab theme={theme} type="button" $active={section === 'credentials'} onClick={() => setSection('credentials')}>
                <KeyRound size={14} /> Credentials
              </SubTab>
              <SubTab theme={theme} type="button" $active={section === 'visibility'} onClick={() => setSection('visibility')}>
                <Eye size={14} /> Visibility
              </SubTab>
              {isEdit && (
                <SubTab theme={theme} type="button" $active={section === 'domain'} onClick={() => setSection('domain')}>
                  <Globe size={14} /> Domain
                </SubTab>
              )}
            </SubTabBar>

            <FormCard theme={theme}>
              {section === 'details' && (
                <>
                  <SectionTitle theme={theme}>Basics</SectionTitle>

                  <FormGroup theme={theme}>
                    <Label theme={theme}>Name *</Label>
                    <Input
                      value={form.name}
                      onChange={set('name')}
                      placeholder="e.g. Fire Inspection Demo"
                    />
                  </FormGroup>

                  <FormGroup theme={theme}>
                    <Label theme={theme}>Demo Slug *</Label>
                    <Input
                      value={form.demoSlug}
                      onChange={handleSlugChange}
                      placeholder="e.g. fire-inspection"
                    />
                    <Hint theme={theme}>Used in the public URL: {demoPublicUrl(slug || '‹slug›')}</Hint>
                  </FormGroup>
                </>
              )}

              {section === 'credentials' && isEdit && (
                <DemoCredentialsPanel orgId={orgId} />
              )}

              {section === 'credentials' && !isEdit && (
                <>
                  <SectionTitle theme={theme}>Viewer login (read-only — shown publicly)</SectionTitle>
                  <Hint theme={theme} style={{ marginTop: 0, marginBottom: theme.spacing.md }}>
                    These public read-only credentials are handed out and shown on the demo landing page.
                    The password is prefilled with the shared standard so every demo's viewer login is the same.
                  </Hint>

                  <FormGroup theme={theme}>
                    <Label theme={theme}>Viewer Login Email *</Label>
                    <Input
                      type="email"
                      value={form.demoLoginEmail}
                      onChange={set('demoLoginEmail')}
                      placeholder={demoViewerEmailDefault(slug || '‹slug›')}
                    />
                  </FormGroup>

                  <FormGroup theme={theme}>
                    <Label theme={theme}>Viewer Login Password *</Label>
                    <Input
                      value={form.demoLoginPassword}
                      onChange={set('demoLoginPassword')}
                      placeholder="Minimum 6 characters"
                    />
                  </FormGroup>

                  <SectionTitle theme={theme}>Builder login (internal)</SectionTitle>
                  <FormGroup theme={theme}>
                    <BuilderInfo theme={theme}>
                      <div><strong>Email:</strong> <code>{demoBuilderEmail(slug || '‹slug›')}</code></div>
                      <div style={{ marginTop: theme.spacing.xs }}>
                        <strong>Password:</strong> the shared demo admin password (set in server config).
                      </div>
                    </BuilderInfo>
                    <Hint theme={theme}>
                      Internal team logs in with this read-write account to build and manage the demo. No builder password is collected here.
                    </Hint>
                  </FormGroup>
                </>
              )}

              {section === 'landing' && (
                <>
                  <SectionTitle theme={theme}>Landing content</SectionTitle>

                  <FormGroup theme={theme}>
                    <Label theme={theme}>Headline</Label>
                    <Input value={form.headline} onChange={set('headline')} placeholder="Demo landing headline" />
                  </FormGroup>

                  <FormGroup theme={theme}>
                    <Label theme={theme}>Subheadline</Label>
                    <Input value={form.subheadline} onChange={set('subheadline')} placeholder="Demo landing subheadline" />
                  </FormGroup>

                  <FormGroup theme={theme}>
                    <Label theme={theme}>Logo URL (optional)</Label>
                    <Input value={form.logo} onChange={set('logo')} placeholder="https://..." />
                  </FormGroup>

                  <SectionTitle theme={theme}>Featured dashboards</SectionTitle>
                  <FormGroup theme={theme}>
                    <Label theme={theme}>Share tokens</Label>
                    <Input
                      type="textarea"
                      rows={4}
                      value={form.featuredTokens}
                      onChange={set('featuredTokens')}
                      placeholder="One dashboard share token per line"
                    />
                    <Hint theme={theme}>Each line is a share token surfaced on the public demo page.</Hint>
                  </FormGroup>
                </>
              )}

              {section === 'visibility' && (
                <>
                  <SectionTitle theme={theme}>Visibility</SectionTitle>
                  <FormGroup theme={theme}>
                    <ToggleSwitch theme={theme}>
                      <ToggleInput
                        type="checkbox"
                        checked={!!form.published}
                        onChange={(e) => setForm(prev => ({ ...prev, published: e.target.checked }))}
                      />
                      <ToggleSlider theme={theme} $checked={!!form.published} />
                      <Label theme={theme} style={{ cursor: 'pointer', marginBottom: 0 }}>Published</Label>
                    </ToggleSwitch>
                    <Hint theme={theme}>When off, the public page renders as a draft.</Hint>
                  </FormGroup>

                  {slug && (
                    <LinkRow theme={theme}>
                      <InlineLink theme={theme} href={demoPublicUrl(slug)} target="_blank" rel="noopener noreferrer">
                        <ExternalLink size={13} /> Public page
                      </InlineLink>
                      <LinkButton theme={theme} type="button" onClick={() => copy(demoLaunchUrl(slug), 'Launch link copied')}>
                        <Copy size={13} /> Copy launch link
                      </LinkButton>
                    </LinkRow>
                  )}
                </>
              )}

              {section === 'domain' && isEdit && (
                <DemoDomainPanel orgId={orgId} slug={slug} />
              )}
            </FormCard>

            <Footer theme={theme}>
              <Button type="button" variant="secondary" onClick={() => navigate('/demo-studio')}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : isEdit ? 'Save Demo' : 'Create Demo'}
              </Button>
            </Footer>
          </form>
        )}
      </PageWrap>
    </Layout>
  );
};

export default DemoEditorPage;
export { DemoEditorPage };
